import { useApiStore } from '@/stores/apiStore';
import { OrganizationApi } from '@/../generated-client/apis/OrganizationApi';
import { OrgNoteApi } from '@/../generated-client/apis/OrgNoteApi';
import {
  AddGroupEmergencyContactAuthIdsRequest,
  AddGroupEmergencyEmailRequest,
  OrganizationAdminUpdate,
  OrganizationAdminUpdateGroupLocationTracking,
  OrganizationSalesCreate,
  OrganizationSalesDelete,
  OrganizationSalesUpdate,
  OrgNoteCreate,
  OrgNoteOnAzure,
  OrgNoteUpdate,
  RemoveGroupEmergencyContactAuthIdsRequest,
  RemoveGroupEmergencyEmailRequest,
  SetUserTierRequest,
  SubscriptionTier,
  UpdateGroupEmergencyEmailLanguageRequest,
} from '../../generated-client/models';

let organizationApi: OrganizationApi | null = null;
let orgNoteApi: OrgNoteApi | null = null;
let lastConfig: unknown = null;

function getOrganizationApi(): OrganizationApi {
  const { apiConfig } = useApiStore.getState();

  // if config changed (token refresh), rebuild
  if (organizationApi === null || lastConfig !== apiConfig) {
    organizationApi = new OrganizationApi(apiConfig);
    lastConfig = apiConfig;
  }

  return organizationApi;
}

function getOrgNoteApi(): OrgNoteApi {
  const { apiConfig } = useApiStore.getState();

  // if config changed (token refresh), rebuild
  if (orgNoteApi === null || lastConfig !== apiConfig) {
    orgNoteApi = new OrgNoteApi(apiConfig);
    lastConfig = apiConfig;
  }

  return orgNoteApi;
}

export const addOrganization = async (
  organization: OrganizationSalesCreate,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationAddPost({
    organizationSalesCreate: organization,
  });
};

export const getAllOrganizationGroupMembers = async (
  orgCode: string,
  azureId: string,
  groupName: string,
) => {
  // const encodedGroupName = encodeURIComponent(groupName);

  const api = getOrganizationApi();

  return await api.apiOrganizationGetGroupMembersGet({
    groupName: groupName,
    orgCode,
  });
};

export const getAllOrganizations = async () => {
  const api = getOrganizationApi();

  return await api.apiOrganizationGetAllGet();
};

export const getOrganization = async (orgCode: string) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationGetByOrgCodeGet({
    orgCode,
  });
};

export const getMiniOrganization = async (orgCode: string) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationGetMiniOrganizationGet({
    orgCode,
  });
};

export const deleteGroup = async (
  groupName: string,
  orgCode: string,
  organizationAzureId: string,
) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationRemoveGroupViaAdminPut({
    organizationAdminRemoveGroup: {
      old_group_name: groupName,
      org_code: orgCode,
      organization_azure_id: organizationAzureId,
    },
  });
};

export const moveGroupMemberViaAdmin = async (
  orgCode: string,
  newGroupName: string,
  toSubscribedList: boolean,
  userAuthId: string,
  orgUserAzureId: string,
) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationMoveGroupMemberViaAdminPut({
    organizationAdminMoveGroupMemberRequest: {
      new_group_name: newGroupName,
      org_code: orgCode,
      user_auth_id: userAuthId,
      to_subscribed_list: toSubscribedList,
    },
  });
};

export const updateOrganizationViaAdmin = async ({
  org_code,
  address,
  company_name,
  email,
  join_org_pin,
  phone_number,
  primary_contact,
  roles_with_features_custom,
  organization_azure_id,
}: OrganizationAdminUpdate) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationUpdateViaAdminPut({
    organizationAdminUpdate: {
      address,
      company_name,
      email,
      join_org_pin,
      org_code,
      organization_azure_id,
      phone_number,
      primary_contact,
      roles_with_features_custom,
    },
  });
};

export const updateOrganizationViaSales = async (
  updateObject: OrganizationSalesUpdate,
) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationUpdateViaSalesPut({
    organizationSalesUpdate: updateObject,
  });
};

export const getIfRequiresPin = async (orgCode: string) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationGetIfRequiresPinGet({
    orgCode,
  });
};

export const getGroupsOpen = async (orgCode: string) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationGetGroupsOpenGet({
    orgCode,
  });
};

export const getGroups = async (orgCode: string, pin?: string) => {
  const api = getOrganizationApi();
  return await api.apiOrganizationGetGroupsGet({
    joinOrgPin: pin,
    orgCode,
  });
};

export const addOrganizationGroup = async (
  orgCode: string,
  groupName: string,
  orgAzureId: string,
) => {
  // Convert the string to raw form to handle backslashes properly
  const normalizedGroupName = String(groupName).replace(/\\+/g, (match) => {
    const count = match.length;
    return '\\'.repeat(Math.floor((count + 1) / 2));
  });

  const api = getOrganizationApi();
  return await api.apiOrganizationAddGroupViaAdminPut({
    organizationAdminAddGroup: {
      new_group_name: normalizedGroupName,
      org_code: orgCode,
      organization_azure_id: orgAzureId,
    },
  });
};

export const requestUpdateSubscriptionViaSales = async (
  orgCode: string,
  orgAzureId: string,
  subscriptionCount: number,
  opsSubscriptionCount: number,
  respondSubscriptionCount: number,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationRequestUpdateSubscriptionViaSalesPut({
    organizationSalesRequestUpdateSubscription: {
      org_code: orgCode,
      organization_azure_id: orgAzureId,
      subscription_count: subscriptionCount,
      ops_subscription_count: opsSubscriptionCount,
      respond_subscription_count: respondSubscriptionCount,
    },
  });
};

export const updateSubscriptionViaSales = async (
  orgCode: string,
  orgAzureId: string,
  subscriptionCount: number,
  opsSubscriptionCount: number,
  respondSubscriptionCount: number,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationUpdateSubscriptionViaSalesPut({
    organizationSalesUpdateSubscription: {
      org_code: orgCode,
      organization_azure_id: orgAzureId,
      subscription_count: subscriptionCount,
      ops_subscription_count: opsSubscriptionCount,
      respond_subscription_count: respondSubscriptionCount,
    },
  });
};

export const setUserTier = async (
  userAuthId: string,
  groupName: string,
  tier: SubscriptionTier,
  targetOrgCode?: string,
) => {
  const api = getOrganizationApi();

  const setUserTierRequest: SetUserTierRequest = {
    user_auth_id: userAuthId,
    group_name: groupName,
    tier,
    target_org_code: targetOrgCode,
  };

  return await api.apiOrganizationSetUserTierPut({
    setUserTierRequest,
  });
};

export const updateGroupName = async (
  orgCode: string,
  oldGroupName: string,
  newGroupName: string,
  orgAzureId: string,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationUpdateGroupNameViaAdminPut({
    organizationAdminUpdateGroupName: {
      new_group_name: newGroupName,
      old_group_name: oldGroupName,
      org_code: orgCode,
      organization_azure_id: orgAzureId,
    },
  });
};

export const getAllOrganizationNotes = async (orgCode: string) => {
  const api = getOrgNoteApi();

  return await api.apiOrgNoteGetAllByOrgCodeGet({
    orgCode,
  });
};

export const updateOrganizationNote = async (note: OrgNoteUpdate) => {
  const api = getOrgNoteApi();

  return await api.apiOrgNoteUpdatePut({
    orgNoteUpdate: note,
  });
};

export const deleteOrganizationNote = async (note: OrgNoteOnAzure) => {
  const api = getOrgNoteApi();

  return await api.apiOrgNoteDeleteDelete({
    orgNoteDelete: {
      org_code: note.org_code,
      org_note_azure_id: note.id,
    },
  });
};

export const addOrganizationNote = async (note: OrgNoteCreate) => {
  const api = getOrgNoteApi();

  return await api.apiOrgNoteAddPost({
    orgNoteCreate: note,
  });
};

export const deleteOrganization = async (
  organization: OrganizationSalesDelete,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationDeleteDelete({
    organizationSalesDelete: organization,
  });
};

export const updateGroupLocationTrackingViaAdmin = async (
  locationObject: OrganizationAdminUpdateGroupLocationTracking,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationUpdateGroupLocationTrackingViaAdminPut({
    organizationAdminUpdateGroupLocationTracking: locationObject,
  });
};

export const addEmergencyEmail = async (
  email: string,
  name: string,
  languageCode: string,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationAddEmergencyEmailViaAdminPut({
    addEmergencyEmailRequest: {
      email,
      name,
      language_code: languageCode,
    },
  });
};

export const removeEmergencyEmail = async (email: string) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationRemoveEmergencyEmailViaAdminDelete({
    removeEmergencyEmailRequest: {
      email,
    },
  });
};

export const getOrgEmergencyContacts = async () => {
  const api = getOrganizationApi();

  return await api.apiOrganizationGetOrgEmergencyContactsGet();
};

export const sendTestEmergencyEmail = async (email: string) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationSendTestEmailPost({
    sendTestEmailRequest: {
      email,
    },
  });
};

export const updateEmergencyEmailLanguage = async (
  email: string,
  languageCode: string,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationUpdateEmergencyEmailLanguageViaAdminPut({
    updateEmergencyEmailLanguageRequest: {
      email,
      language_code: languageCode,
    },
  });
};

// ============================================================
// Group Emergency Contact Functions
// ============================================================

export const getGroupEmergencyContacts = async (groupId: string) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationGetGroupEmergencyContactsGet({
    groupId,
  });
};

export const getAllGroupEmergencyContacts = async (orgCode?: string) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationGetAllGroupEmergencyContactsGet({
    orgCode,
  });
};

export const addGroupEmergencyContactAuthIds = async (
  request: AddGroupEmergencyContactAuthIdsRequest,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationAddGroupEmergencyContactAuthIdsPut({
    addGroupEmergencyContactAuthIdsRequest: request,
  });
};

export const removeGroupEmergencyContactAuthIds = async (
  request: RemoveGroupEmergencyContactAuthIdsRequest,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationRemoveGroupEmergencyContactAuthIdsDelete({
    removeGroupEmergencyContactAuthIdsRequest: request,
  });
};

export const addGroupEmergencyEmail = async (
  request: AddGroupEmergencyEmailRequest,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationAddGroupEmergencyEmailPut({
    addGroupEmergencyEmailRequest: request,
  });
};

export const removeGroupEmergencyEmail = async (
  request: RemoveGroupEmergencyEmailRequest,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationRemoveGroupEmergencyEmailDelete({
    removeGroupEmergencyEmailRequest: request,
  });
};

export const updateGroupEmergencyEmailLanguage = async (
  request: UpdateGroupEmergencyEmailLanguageRequest,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationUpdateGroupEmergencyEmailLanguagePut({
    updateGroupEmergencyEmailLanguageRequest: request,
  });
};

// ============================================================
// Organization-Level Emergency Contact Auth ID Functions
// ============================================================

export const addEmergencyContactAuthIds = async (authIds: string[]) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationAddEmergencyContactAuthIdsPut({
    addEmergencyContactAuthIdsRequest: {
      auth_ids: authIds,
    },
  });
};

export const removeEmergencyContactAuthIds = async (authIds: string[]) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationRemoveEmergencyContactAuthIdsDelete({
    removeEmergencyContactAuthIdsRequest: {
      auth_ids: authIds,
    },
  });
};

export const toggleAddNewAdminsToEmergencyContacts = async (
  enabled: boolean,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationToggleAddNewAdminsToEmergencyContactsPut({
    enabled,
  });
};

export const toggleAddNewMembersToEmergencyContacts = async (
  groupId: string,
  enabled: boolean,
) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationToggleAddNewMembersToEmergencyContactsPut({
    groupId,
    enabled,
  });
};

export const setRequireJoinApproval = async (requireJoinApproval: boolean) => {
  const api = getOrganizationApi();

  return await api.apiOrganizationSetRequireJoinApprovalPut({
    setRequireJoinApprovalRequest: {
      require_join_approval: requireJoinApproval,
    },
  });
};
